import React, { useState, useEffect } from "react";
import {
  collection, addDoc, getDocs, updateDoc, deleteDoc,
  doc, serverTimestamp, query, orderBy, setDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";
import DateBox from "../crm/DateBox";
import { fmtD } from "../crm/dateFormat";

// ─── Vijay-only access ────────────────────────────────────────────────────────
const VIJAY_NUMBERS = ["9494284529", "+919494284529"];
const isVijay = (user) => {
  if (!user) return false;
  const val = user.phone || user.mobile || user.id || user.uid || user.mobile2 || "";
  return VIJAY_NUMBERS.includes(String(val).trim());
};

// ─── Constants ────────────────────────────────────────────────────────────────
const GOSHALA_PCT = 0.10;
const MOTHER_PCT  = 0.10;

const TABS = [
  { key: "checklist", label: "✅ Checklist" },
  { key: "daily",     label: "📒 Daily Tracker" },
  { key: "forecast",  label: "📈 Forecaster" },
  { key: "summary",   label: "📊 Summary" },
];

const PHASES = ["Phase 1 — Now", "Phase 2 — On Job", "Phase 3 — Revenue"];
const STATUS_CYCLE = { pending: "inprog", inprog: "done", done: "pending" };
const STATUS_LABEL = { pending: "Pending", inprog: "In Progress", done: "Done" };
const STATUS_COLOR = { pending: "#fbbf24", inprog: "#4f8ef7", done: "#3ecf8e" };
const PRIORITY_COLOR = { "🔴 High": "#f87171", "🟡 Medium": "#fbbf24", "🟢 Low": "#3ecf8e" };

const CATEGORIES = [
  "Legal / Registration", "Trademark / Patent", "Technology",
  "Marketing", "Salary", "Platform Revenue", "Consulting", "Other",
];

// ─── Seed checklist (all 14 real tasks from HTML) ─────────────────────────────
const SEED_CHECKLIST = [
  { phase: "Phase 1 — Now", task: "Document your platform idea", cost: "FREE", priority: "🔴 High", status: "pending" },
  { phase: "Phase 1 — Now", task: "Email idea to yourself (timestamp)", cost: "FREE", priority: "🔴 High", status: "pending" },
  { phase: "Phase 1 — Now", task: "Research brand/platform name", cost: "FREE", priority: "🔴 High", status: "pending" },
  { phase: "Phase 1 — Now", task: "Start building the platform (MVP)", cost: "FREE", priority: "🔴 High", status: "pending" },
  { phase: "Phase 2 — On Job", task: "Disclose patent to employer, get written exception", cost: "FREE", priority: "🔴 High", status: "pending" },
  { phase: "Phase 2 — On Job", task: "Register LLP (you + spouse)", cost: "₹8,000–17,000", priority: "🔴 High", status: "pending" },
  { phase: "Phase 2 — On Job", task: "Get logo designed", cost: "₹2,000–10,000", priority: "🟡 Medium", status: "pending" },
  { phase: "Phase 2 — On Job", task: "Apply for Composite TM (Text + Logo)", cost: "₹4,500–9,000", priority: "🔴 High", status: "pending" },
  { phase: "Phase 2 — On Job", task: "File Patent in your name", cost: "₹28,000–75,000", priority: "🔴 High", status: "pending" },
  { phase: "Phase 2 — On Job", task: "Sign Licensing Agreement (you ↔ LLP)", cost: "₹5,000–15,000", priority: "🟡 Medium", status: "pending" },
  { phase: "Phase 2 — On Job", task: "Draft Will (patent → spouse after demise)", cost: "₹3,000–10,000", priority: "🔴 High", status: "pending" },
  { phase: "Phase 3 — Revenue", task: "Convert LLP to Pvt Ltd", cost: "₹15,000–25,000", priority: "🟡 Medium", status: "pending" },
  { phase: "Phase 3 — Revenue", task: "Set up profit distribution (Goshala + Mother)", cost: "CA fees", priority: "🔴 High", status: "pending" },
  { phase: "Phase 3 — Revenue", task: "Hire CA for annual compliance", cost: "₹20,000–50,000/yr", priority: "🟡 Medium", status: "pending" },
];

// ─── Seed forecaster (one doc with all named fields, stored in Firestore) ─────
const SEED_FORECAST_ID = "forecast_main";
const SEED_FORECAST = {
  platformSubscription: 0,
  licensingIncome: 0,
  consulting: 0,
  otherIncome: 0,
  serverInfra: 0,
  legalCompliance: 0,
  salaries: 0,
  marketing: 0,
  otherExpenses: 0,
};

// ─── Static Summary data (read-only, no DB needed) ────────────────────────────
const BLUEPRINT = [
  { area: "Entity Type",      decision: "Start with LLP → Convert to Pvt Ltd on revenue" },
  { area: "Ownership",        decision: "Both you & spouse as LLP partners" },
  { area: "Patent",           decision: "In YOUR name — boosts professional value" },
  { area: "Trademark",        decision: "Composite (Text + Logo) under company name" },
  { area: "Patent Licensing", decision: "Licensing agreement between you & LLP" },
  { area: "Succession",       decision: "Patent & shares transfer to spouse via Will" },
  { area: "Goshala",          decision: "10% of net profit — separate entity" },
  { area: "Mother",           decision: "10% of net profit — monthly/quarterly" },
  { area: "Job Disclosure",   decision: "Disclose patent, get written exception in contract" },
];

const BUDGET = [
  { phase: "Phase 1", when: "Now (Jobless)",    cost: "₹0",                      items: "Document idea, email timestamp, research name", accent: "#3ecf8e" },
  { phase: "Phase 2", when: "On getting job",   cost: "₹47,500 – ₹1,26,000",    items: "LLP + TM + Patent + Logo + Will + Licensing", accent: "#e8eaf0" },
  { phase: "Phase 3", when: "On revenue",        cost: "₹37,000 – ₹80,000+/yr",  items: "Pvt Ltd conversion + CA compliance + distributions", accent: "#e8eaf0" },
  { phase: "Total",   when: "3 Years",           cost: "₹84,500 – ₹2,06,000",    items: "Mostly self-funded from business revenue", accent: "#4f8ef7" },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const inr = (n) => "₹" + (Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtDate = (ts) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return fmtD(d);
};

// ─── Blank forms ─────────────────────────────────────────────────────────────
const blankChecklist = () => ({ phase: "Phase 1 — Now", task: "", cost: "", priority: "🔴 High", status: "pending" });
const blankDaily     = () => ({ date: new Date().toISOString().slice(0, 10), description: "", amount: "", type: "Expense", category: "Other" });

// ═════════════════════════════════════════════════════════════════════════════
// Main Component
// ═════════════════════════════════════════════════════════════════════════════
const MyTrackerScreen = ({ user }) => {
  const [activeTab,     setActiveTab]     = useState("checklist");
  const [checklist,     setChecklist]     = useState([]);
  const [dailyEntries,  setDailyEntries]  = useState([]);
  const [forecast,      setForecast]      = useState(SEED_FORECAST);
  const [loading,       setLoading]       = useState(true);
  const [showForm,      setShowForm]      = useState(false);
  const [editId,        setEditId]        = useState(null);
  const [checklistForm, setChecklistForm] = useState(blankChecklist());
  const [dailyForm,     setDailyForm]     = useState(blankDaily());
  const [saving,        setSaving]        = useState(false);
  const [msg,           setMsg]           = useState("");

  // ── Load data ───────────────────────────────────────────────────────────────
  const loadAll = async () => {
    setLoading(true);
    try {
      // Checklist
      const clSnap = await getDocs(query(collection(db, "myTrackerChecklist"), orderBy("createdAt", "asc")));
      setChecklist(clSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Daily
      const dlSnap = await getDocs(query(collection(db, "myTrackerDaily"), orderBy("date", "desc")));
      setDailyEntries(dlSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Forecast (single doc)
      const fcSnap = await getDocs(collection(db, "myTrackerForecast"));
      if (!fcSnap.empty) {
        const data = fcSnap.docs[0].data();
        setForecast({ ...SEED_FORECAST, ...data });
      }
    } catch (e) {
      setMsg("Error loading data: " + e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    const init = async () => {
      try {
        // Seed checklist only if empty
        const snap = await getDocs(collection(db, "myTrackerChecklist"));
        if (snap.empty) {
          for (const item of SEED_CHECKLIST) {
            await addDoc(collection(db, "myTrackerChecklist"), { ...item, createdAt: serverTimestamp() });
          }
        }
        // Seed forecast doc only if empty
        const fcSnap = await getDocs(collection(db, "myTrackerForecast"));
        if (fcSnap.empty) {
          await setDoc(doc(db, "myTrackerForecast", SEED_FORECAST_ID), { ...SEED_FORECAST, createdAt: serverTimestamp() });
        }
      } catch (e) {}
      await loadAll();
    };
    init();
  }, []);

  if (!isVijay(user)) return null;

  // ── Checklist: cycle status (3-state) ──────────────────────────────────────
  const cycleStatus = async (entry) => {
    const next = STATUS_CYCLE[entry.status] || "pending";
    try {
      await updateDoc(doc(db, "myTrackerChecklist", entry.id), { status: next, lastUpdatedAt: serverTimestamp() });
      await loadAll();
    } catch (e) { setMsg("Error: " + e.message); }
  };

  // ── Checklist: save ────────────────────────────────────────────────────────
  const saveChecklist = async () => {
    if (!checklistForm.task.trim()) { setMsg("Please enter a task name."); return; }
    setSaving(true);
    try {
      if (editId) {
        await updateDoc(doc(db, "myTrackerChecklist", editId), { ...checklistForm, lastUpdatedAt: serverTimestamp() });
        setMsg("Task updated.");
      } else {
        await addDoc(collection(db, "myTrackerChecklist"), { ...checklistForm, createdAt: serverTimestamp() });
        setMsg("Task added.");
      }
      setChecklistForm(blankChecklist()); setEditId(null); setShowForm(false);
      await loadAll();
    } catch (e) { setMsg("Save error: " + e.message); }
    setSaving(false);
  };

  // ── Daily: save ────────────────────────────────────────────────────────────
  const saveDaily = async () => {
    if (!dailyForm.description.trim() || !dailyForm.amount) { setMsg("Please enter description and amount."); return; }
    setSaving(true);
    try {
      const entry = { ...dailyForm, amount: Number(dailyForm.amount) || 0 };
      if (editId) {
        await updateDoc(doc(db, "myTrackerDaily", editId), { ...entry, lastUpdatedAt: serverTimestamp() });
        setMsg("Entry updated.");
      } else {
        await addDoc(collection(db, "myTrackerDaily"), { ...entry, createdAt: serverTimestamp() });
        setMsg("Entry added.");
      }
      setDailyForm(blankDaily()); setEditId(null); setShowForm(false);
      await loadAll();
    } catch (e) { setMsg("Save error: " + e.message); }
    setSaving(false);
  };

  // ── Forecast: save to Firestore on every field change ──────────────────────
  const updateForecast = async (field, value) => {
    const updated = { ...forecast, [field]: Number(value) || 0 };
    setForecast(updated);
    try {
      await setDoc(doc(db, "myTrackerForecast", SEED_FORECAST_ID), { ...updated, lastUpdatedAt: serverTimestamp() }, { merge: true });
    } catch (e) {}
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (colName, id) => {
    if (!window.confirm("Delete this entry? Cannot be undone.")) return;
    try {
      await deleteDoc(doc(db, colName, id));
      setMsg("Deleted."); await loadAll();
    } catch (e) { setMsg("Delete error: " + e.message); }
  };

  // ── Checklist metrics ──────────────────────────────────────────────────────
  const clTotal   = checklist.length;
  const clDone    = checklist.filter(i => i.status === "done").length;
  const clInprog  = checklist.filter(i => i.status === "inprog").length;
  const clPending = checklist.filter(i => i.status === "pending").length;

  // ── Daily metrics — smart FY logic ────────────────────────────────────────
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().slice(0, 10);

  // FY runs Apr 1 – Mar 31. Determine current FY start and end.
  const fyStartYear  = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  const fyStart      = new Date(fyStartYear, 3, 1);       // Apr 1 of current FY
  const fyEnd        = new Date(fyStartYear + 1, 2, 31);  // Mar 31 of next year
  const fyStartStr   = fyStart.toISOString().slice(0, 10);
  const fyEndStr     = fyEnd.toISOString().slice(0, 10);

  // Mid-FY: show from today → Mar 31. Next FY onwards: full Apr–Mar.
  const isMidFY      = today > fyStart; // we joined after FY started
  const periodStart  = isMidFY ? todayStr : fyStartStr;
  const periodLabel  = isMidFY ? `${todayStr} – ${fyEndStr}` : `${fyStartStr} – ${fyEndStr}`;

  // Filter helpers
  const inPeriod = (e) => e.date >= periodStart && e.date <= fyEndStr;
  const inYTD    = (e) => e.date >= fyStartStr  && e.date <= todayStr;

  const fyIncome   = dailyEntries.filter(e => e.type === "Income"  && inPeriod(e)).reduce((s, e) => s + Number(e.amount || 0), 0);
  const fyExpense  = dailyEntries.filter(e => e.type === "Expense" && inPeriod(e)).reduce((s, e) => s + Number(e.amount || 0), 0);
  const fyNet      = fyIncome - fyExpense;
  const ytdIncome  = dailyEntries.filter(e => e.type === "Income"  && inYTD(e)).reduce((s, e) => s + Number(e.amount || 0), 0);
  const ytdExpense = dailyEntries.filter(e => e.type === "Expense" && inYTD(e)).reduce((s, e) => s + Number(e.amount || 0), 0);
  const ytdNet     = ytdIncome - ytdExpense;

  // ── Forecast calc ──────────────────────────────────────────────────────────
  const grossRev   = (forecast.platformSubscription || 0) + (forecast.licensingIncome || 0) + (forecast.consulting || 0) + (forecast.otherIncome || 0);
  const totalCosts = (forecast.serverInfra || 0) + (forecast.legalCompliance || 0) + (forecast.salaries || 0) + (forecast.marketing || 0) + (forecast.otherExpenses || 0);
  const netProfit  = grossRev - totalCosts;

  // ─────────────────────────────────────────────────────────────────────────
  // Styles
  // ─────────────────────────────────────────────────────────────────────────
  const S = {
    wrap:       { padding: "16px", maxWidth: "900px", fontFamily: "sans-serif", color: "#e8eaf0" },
    header:     { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "8px" },
    title:      { fontSize: "18px", fontWeight: "700", color: "#a78bfa" },
    tabs:       { display: "flex", gap: "4px", marginBottom: "16px", flexWrap: "wrap" },
    tab:        (a) => ({
      padding: "8px 16px", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600",
      background: a ? "#4f8ef7" : "#1e2235", color: a ? "#fff" : "#7b82a0",
      border: `1px solid ${a ? "#4f8ef7" : "#2e3250"}`,
    }),
    metricRow:  { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "12px", marginBottom: "20px" },
    metricCard: (accent) => ({
      background: "#1a1d27", border: `1px solid #2e3250`, borderRadius: "10px", padding: "14px",
      borderTop: `3px solid ${accent || "#4f8ef7"}`,
    }),
    metricLbl:  { fontSize: "11px", color: "#7b82a0", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "6px" },
    metricVal:  (color) => ({ fontSize: "20px", fontWeight: "700", color: color || "#e8eaf0", fontFamily: "monospace" }),
    metricSub:  { fontSize: "11px", color: "#7b82a0", marginTop: "3px" },
    section:    { fontSize: "12px", fontWeight: "700", color: "#7b82a0", textTransform: "uppercase", letterSpacing: "0.8px", margin: "20px 0 10px" },
    tableWrap:  { background: "#1a1d27", border: "1px solid #2e3250", borderRadius: "10px", overflow: "hidden", marginBottom: "16px" },
    th:         { padding: "10px 14px", fontSize: "11px", fontWeight: "600", color: "#7b82a0", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "1px solid #2e3250", background: "#222638", textAlign: "left" },
    td:         { padding: "11px 14px", fontSize: "13px", borderBottom: "1px solid #2e3250", verticalAlign: "middle" },
    badge:      (status) => ({
      display: "inline-block", padding: "3px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: "600",
      cursor: "pointer", background: `${STATUS_COLOR[status]}22`, color: STATUS_COLOR[status],
      border: `1px solid ${STATUS_COLOR[status]}55`,
    }),
    priority:   (p) => ({ fontSize: "11px", fontWeight: "600", color: PRIORITY_COLOR[p] || "#ccc" }),
    form:       { background: "#1a1d27", border: "1px solid #2e3250", borderRadius: "10px", padding: "16px", marginBottom: "16px" },
    formGrid:   { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "12px", marginBottom: "14px" },
    label:      { display: "block", fontSize: "11px", color: "#7b82a0", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "5px" },
    input:      { width: "100%", background: "#222638", border: "1px solid #2e3250", borderRadius: "7px", color: "#e8eaf0", fontSize: "13px", padding: "8px 10px", outline: "none", boxSizing: "border-box" },
    select:     { width: "100%", background: "#222638", border: "1px solid #2e3250", borderRadius: "7px", color: "#e8eaf0", fontSize: "13px", padding: "8px 10px", outline: "none", boxSizing: "border-box" },
    btnBlue:    { background: "#4f8ef7", color: "#fff", border: "none", borderRadius: "7px", padding: "9px 18px", fontSize: "13px", fontWeight: "600", cursor: "pointer" },
    btnGray:    { background: "#222638", color: "#ccc", border: "1px solid #2e3250", borderRadius: "7px", padding: "9px 14px", fontSize: "13px", cursor: "pointer" },
    btnRed:     { background: "rgba(248,113,113,0.15)", color: "#f87171", border: "1px solid rgba(248,113,113,0.3)", borderRadius: "7px", padding: "5px 10px", fontSize: "11px", cursor: "pointer" },
    btnSmall:   { background: "#222638", color: "#aaa", border: "1px solid #2e3250", borderRadius: "6px", padding: "4px 10px", fontSize: "11px", cursor: "pointer" },
    msg:        { background: "#222638", border: "1px solid #4f8ef7", borderRadius: "8px", padding: "10px 14px", marginBottom: "12px", fontSize: "13px", color: "#4f8ef7" },
    fcastBox:   { background: "#1a1d27", border: "1px solid #2e3250", borderRadius: "10px", padding: "18px", marginBottom: "16px" },
    fcastTitle: { fontSize: "12px", fontWeight: "600", color: "#7b82a0", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "14px" },
    fcastRow:   { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #2e3250" },
    fcastLbl:   { fontSize: "13px", color: "#e8eaf0" },
    fcastInput: { width: "140px", background: "#222638", border: "1px solid #2e3250", borderRadius: "6px", color: "#e8eaf0", fontSize: "13px", padding: "6px 10px", textAlign: "right", outline: "none", fontFamily: "monospace" },
    resultRow:  { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px", background: "#222638", borderRadius: "8px", marginTop: "10px" },
    distRow:    { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid #2e3250", fontSize: "13px" },
    distDot:    (c) => ({ width: "10px", height: "10px", borderRadius: "50%", background: c, marginRight: "10px", display: "inline-block" }),
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={S.wrap}>

      {/* Header */}
      <div style={S.header}>
        <div style={S.title}>💼 My Tracker</div>
        <div style={{ fontSize: "11px", color: "#7b82a0", background: "#222638", padding: "5px 12px", borderRadius: "6px", border: "1px solid #2e3250", fontFamily: "monospace" }}>
          {new Date().toLocaleDateString("en-IN", { weekday: "short", timeZone: "Asia/Kolkata" }) + ", " + fmtD(new Date())}
        </div>
      </div>

      {/* Tabs */}
      <div style={S.tabs}>
        {TABS.map(t => (
          <div key={t.key} style={S.tab(activeTab === t.key)} onClick={() => { setActiveTab(t.key); setShowForm(false); setEditId(null); }}>
            {t.label}
          </div>
        ))}
      </div>

      {msg && <div style={S.msg}>{msg} <span style={{ float: "right", cursor: "pointer" }} onClick={() => setMsg("")}>✖</span></div>}

      {loading ? <p style={{ color: "#777" }}>Loading…</p> : (
        <>
          {/* ══════════ CHECKLIST TAB ══════════ */}
          {activeTab === "checklist" && (
            <>
              {/* Metric cards */}
              <div style={S.metricRow}>
                <div style={S.metricCard("#4f8ef7")}><div style={S.metricLbl}>Total Tasks</div><div style={S.metricVal()}>{clTotal}</div><div style={S.metricSub}>across all phases</div></div>
                <div style={S.metricCard("#3ecf8e")}><div style={S.metricLbl}>Completed</div><div style={S.metricVal("#3ecf8e")}>{clDone}</div><div style={S.metricSub}>tasks done</div></div>
                <div style={S.metricCard("#fbbf24")}><div style={S.metricLbl}>In Progress</div><div style={S.metricVal("#fbbf24")}>{clInprog}</div><div style={S.metricSub}>tasks ongoing</div></div>
                <div style={S.metricCard("#a78bfa")}><div style={S.metricLbl}>Pending</div><div style={S.metricVal("#a78bfa")}>{clPending}</div><div style={S.metricSub}>tasks remaining</div></div>
              </div>

              {/* Per-phase tables — accepts old ("Phase 1") and new ("Phase 1 — Now") formats */}
              {PHASES.map(phase => {
                const phaseShort = phase.split(" — ")[0]; // e.g. "Phase 1"
                const items = checklist.filter(i => {
                  const p = i.phase || "";
                  return p === phase || p === phaseShort;
                });
                if (!items.length) return null;
                return (
                  <div key={phase}>
                    <div style={S.section}>{phase}</div>
                    <div style={S.tableWrap}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead><tr>
                          <th style={S.th}>#</th>
                          <th style={S.th}>Task</th>
                          <th style={S.th}>Est. Cost</th>
                          <th style={S.th}>Priority</th>
                          <th style={S.th}>Status</th>
                          <th style={S.th}></th>
                        </tr></thead>
                        <tbody>
                          {items.map((item, idx) => {
                            const taskLabel = item.task || item.title || "—";
                            return (
                            <tr key={item.id} style={{ background: "transparent" }}>
                              <td style={{ ...S.td, color: "#7b82a0", fontFamily: "monospace", fontSize: "11px" }}>{idx + 1}</td>
                              <td style={S.td}>{taskLabel}</td>
                              <td style={{ ...S.td, fontFamily: "monospace", fontSize: "12px" }}>{item.cost || "—"}</td>
                              <td style={S.td}><span style={S.priority(item.priority)}>{item.priority || "—"}</span></td>
                              <td style={S.td}>
                                <span style={S.badge(item.status || "pending")} onClick={() => cycleStatus(item)}>
                                  {STATUS_LABEL[item.status] || "Pending"}
                                </span>
                              </td>
                              <td style={S.td}>
                                <div style={{ display: "flex", gap: "6px" }}>
                                  <button style={S.btnSmall} onClick={() => {
                                    setChecklistForm({ phase: item.phase, task: taskLabel, cost: item.cost || "", priority: item.priority || "🔴 High", status: item.status || "pending" });
                                    setEditId(item.id); setShowForm(true);
                                  }}>✏️</button>
                                  <button style={S.btnRed} onClick={() => handleDelete("myTrackerChecklist", item.id)}>✕</button>
                                </div>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}

              {/* Add task button */}
              <button style={S.btnGray} onClick={() => { setShowForm(!showForm); setEditId(null); setChecklistForm(blankChecklist()); }}>
                {showForm ? "✖ Cancel" : "+ Add Task"}
              </button>

              {/* Add/Edit form */}
              {showForm && (
                <div style={{ ...S.form, marginTop: "12px" }}>
                  <div style={S.formGrid}>
                    <div>
                      <label style={S.label}>Phase</label>
                      <select style={S.select} value={checklistForm.phase} onChange={e => setChecklistForm({ ...checklistForm, phase: e.target.value })}>
                        {PHASES.map(p => <option key={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={S.label}>Task *</label>
                      <input style={S.input} placeholder="e.g. Register LLP" value={checklistForm.task} onChange={e => setChecklistForm({ ...checklistForm, task: e.target.value })} />
                    </div>
                    <div>
                      <label style={S.label}>Est. Cost</label>
                      <input style={S.input} placeholder="e.g. ₹15,000" value={checklistForm.cost} onChange={e => setChecklistForm({ ...checklistForm, cost: e.target.value })} />
                    </div>
                    <div>
                      <label style={S.label}>Priority</label>
                      <select style={S.select} value={checklistForm.priority} onChange={e => setChecklistForm({ ...checklistForm, priority: e.target.value })}>
                        <option>🔴 High</option><option>🟡 Medium</option><option>🟢 Low</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button style={S.btnBlue} onClick={saveChecklist} disabled={saving}>{saving ? "Saving…" : editId ? "💾 Update Task" : "💾 Save Task"}</button>
                    <button style={S.btnGray} onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ══════════ DAILY TRACKER TAB ══════════ */}
          {activeTab === "daily" && (
            <>
              {/* 4 Metric cards — smart FY logic */}
              <div style={{ fontSize: "11px", color: "#7b82a0", marginBottom: "12px", fontFamily: "monospace" }}>
                📅 Period: {periodLabel}
              </div>
              <div style={S.metricRow}>
                <div style={S.metricCard("#3ecf8e")}><div style={S.metricLbl}>Income (FY)</div><div style={S.metricVal("#3ecf8e")}>{inr(fyIncome)}</div><div style={S.metricSub}>{isMidFY ? "today onwards" : "full FY"}</div></div>
                <div style={S.metricCard("#f87171")}><div style={S.metricLbl}>Expense (FY)</div><div style={S.metricVal("#f87171")}>{inr(fyExpense)}</div><div style={S.metricSub}>{isMidFY ? "today onwards" : "full FY"}</div></div>
                <div style={S.metricCard("#fbbf24")}><div style={S.metricLbl}>Net (FY)</div><div style={S.metricVal(fyNet >= 0 ? "#3ecf8e" : "#f87171")}>{inr(Math.abs(fyNet))}</div><div style={S.metricSub}>{fyNet >= 0 ? "surplus" : "deficit"}</div></div>
                <div style={S.metricCard("#4f8ef7")}><div style={S.metricLbl}>YTD Net</div><div style={S.metricVal(ytdNet >= 0 ? "#3ecf8e" : "#f87171")}>{inr(Math.abs(ytdNet))}</div><div style={S.metricSub}>Apr 1 – today</div></div>
              </div>

              {/* Add button */}
              <button style={{ ...S.btnBlue, marginBottom: "14px" }} onClick={() => { setShowForm(!showForm); setEditId(null); setDailyForm(blankDaily()); }}>
                {showForm ? "✖ Cancel" : "+ Add Entry"}
              </button>

              {/* Add/Edit form */}
              {showForm && (
                <div style={{ ...S.form, marginBottom: "14px" }}>
                  <div style={S.formGrid}>
                    <div>
                      <label style={S.label}>Date *</label>
                      <DateBox value={dailyForm.date} onChange={(v) => setDailyForm({ ...dailyForm, date: v })} />
                    </div>
                    <div>
                      <label style={S.label}>Description *</label>
                      <input style={S.input} placeholder="e.g. LLP Registration fee" value={dailyForm.description} onChange={e => setDailyForm({ ...dailyForm, description: e.target.value })} />
                    </div>
                    <div>
                      <label style={S.label}>Amount (₹) *</label>
                      <input style={S.input} type="number" placeholder="0" value={dailyForm.amount} onChange={e => setDailyForm({ ...dailyForm, amount: e.target.value })} />
                    </div>
                    <div>
                      <label style={S.label}>Type</label>
                      <select style={S.select} value={dailyForm.type} onChange={e => setDailyForm({ ...dailyForm, type: e.target.value })}>
                        <option>Expense</option><option>Income</option>
                      </select>
                    </div>
                    <div>
                      <label style={S.label}>Category</label>
                      <select style={S.select} value={dailyForm.category} onChange={e => setDailyForm({ ...dailyForm, category: e.target.value })}>
                        {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button style={S.btnBlue} onClick={saveDaily} disabled={saving}>{saving ? "Saving…" : editId ? "💾 Update Entry" : "💾 Save Entry"}</button>
                    <button style={S.btnGray} onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</button>
                  </div>
                </div>
              )}

              {/* Table */}
              {dailyEntries.length === 0 ? (
                <p style={{ color: "#777", textAlign: "center", padding: "30px" }}>📒 No entries yet. Add your first transaction above.</p>
              ) : (
                <div style={S.tableWrap}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr>
                      <th style={S.th}>Date</th>
                      <th style={S.th}>Description</th>
                      <th style={S.th}>Category</th>
                      <th style={S.th}>Type</th>
                      <th style={S.th}>Amount</th>
                      <th style={S.th}></th>
                    </tr></thead>
                    <tbody>
                      {dailyEntries.map((e, i) => {
                        const isIncome = e.type === "Income";
                        return (
                          <tr key={e.id}>
                            <td style={{ ...S.td, fontFamily: "monospace", fontSize: "12px", color: "#7b82a0" }}>{fmtD(e.date)}</td>
                            <td style={S.td}>{e.description}</td>
                            <td style={S.td}><span style={{ background: "#1e2a4a", color: "#4f8ef7", padding: "2px 8px", borderRadius: "12px", fontSize: "11px" }}>{e.category}</span></td>
                            <td style={{ ...S.td, color: isIncome ? "#3ecf8e" : "#f87171", fontWeight: "600", fontSize: "12px" }}>{e.type}</td>
                            <td style={{ ...S.td, fontFamily: "monospace", fontWeight: "600", color: isIncome ? "#3ecf8e" : "#f87171" }}>{isIncome ? "+" : "-"}{inr(e.amount)}</td>
                            <td style={S.td}>
                              <div style={{ display: "flex", gap: "6px" }}>
                                <button style={S.btnSmall} onClick={() => {
                                  setDailyForm({ date: e.date, description: e.description, amount: e.amount, type: e.type, category: e.category });
                                  setEditId(e.id); setShowForm(true);
                                }}>✏️</button>
                                <button style={S.btnRed} onClick={() => handleDelete("myTrackerDaily", e.id)}>✕</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* ══════════ FORECASTER TAB ══════════ */}
          {activeTab === "forecast" && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px" }}>

                {/* Revenue */}
                <div style={S.fcastBox}>
                  <div style={S.fcastTitle}>📥 Revenue Projections</div>
                  {[
                    ["platformSubscription", "Platform Subscription Revenue"],
                    ["licensingIncome",       "Licensing Income (Patent)"],
                    ["consulting",            "Consulting / Services"],
                    ["otherIncome",           "Other Income"],
                  ].map(([field, label]) => (
                    <div key={field} style={S.fcastRow}>
                      <span style={S.fcastLbl}>{label}</span>
                      <input style={S.fcastInput} type="number" value={forecast[field] || 0}
                        onChange={e => updateForecast(field, e.target.value)} />
                    </div>
                  ))}
                  <div style={S.resultRow}>
                    <span style={{ fontSize: "13px", color: "#7b82a0" }}>Gross Revenue</span>
                    <span style={{ fontFamily: "monospace", fontSize: "15px", fontWeight: "700", color: "#3ecf8e" }}>{inr(grossRev)}</span>
                  </div>
                </div>

                {/* Costs */}
                <div style={S.fcastBox}>
                  <div style={S.fcastTitle}>📤 Cost Projections</div>
                  {[
                    ["serverInfra",      "Server / Infrastructure"],
                    ["legalCompliance",  "Legal & Compliance"],
                    ["salaries",         "Salaries / Freelancers"],
                    ["marketing",        "Marketing"],
                    ["otherExpenses",    "Other Expenses"],
                  ].map(([field, label]) => (
                    <div key={field} style={S.fcastRow}>
                      <span style={S.fcastLbl}>{label}</span>
                      <input style={S.fcastInput} type="number" value={forecast[field] || 0}
                        onChange={e => updateForecast(field, e.target.value)} />
                    </div>
                  ))}
                  <div style={S.resultRow}>
                    <span style={{ fontSize: "13px", color: "#7b82a0" }}>Total Costs</span>
                    <span style={{ fontFamily: "monospace", fontSize: "15px", fontWeight: "700", color: "#f87171" }}>{inr(totalCosts)}</span>
                  </div>
                </div>
              </div>

              {/* Profit Distribution */}
              <div style={{ ...S.fcastBox, marginTop: "0" }}>
                <div style={S.fcastTitle}>💰 Profit Distribution</div>
                <div style={S.resultRow}>
                  <span style={{ fontSize: "13px", color: "#7b82a0" }}>Net Profit Before Distribution</span>
                  <span style={{ fontFamily: "monospace", fontSize: "15px", fontWeight: "700", color: "#4f8ef7" }}>{inr(netProfit)}</span>
                </div>
                <div style={{ marginTop: "12px" }}>
                  {[
                    ["#fbbf24", "Goshala (10%)", netProfit * GOSHALA_PCT],
                    ["#a78bfa", "Mother (10%)",  netProfit * MOTHER_PCT],
                    ["#3ecf8e", "You & Spouse (80%)", netProfit * 0.80],
                  ].map(([color, label, amount]) => (
                    <div key={label} style={S.distRow}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <span style={S.distDot(color)}></span>
                        <span>{label}</span>
                      </div>
                      <span style={{ fontFamily: "monospace", fontWeight: "700", color }}>{inr(Math.max(0, amount))}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ══════════ SUMMARY TAB ══════════ */}
          {activeTab === "summary" && (
            <>
              <div style={S.section}>Your Startup Blueprint</div>
              <div style={S.tableWrap}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>
                    <th style={S.th}>Area</th>
                    <th style={S.th}>Decision</th>
                    <th style={S.th}>Status</th>
                  </tr></thead>
                  <tbody>
                    {BLUEPRINT.map(row => (
                      <tr key={row.area}>
                        <td style={{ ...S.td, fontWeight: "600" }}>{row.area}</td>
                        <td style={{ ...S.td, color: "#ccc" }}>{row.decision}</td>
                        <td style={S.td}><span style={S.badge("pending")}>Pending</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={S.section}>Estimated Budget (3 Years)</div>
              <div style={S.tableWrap}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>
                    <th style={S.th}>Phase</th>
                    <th style={S.th}>When</th>
                    <th style={S.th}>Estimated Cost</th>
                    <th style={S.th}>Key Items</th>
                  </tr></thead>
                  <tbody>
                    {BUDGET.map(row => (
                      <tr key={row.phase}>
                        <td style={{ ...S.td, fontWeight: row.phase === "Total" ? "700" : "400" }}>{row.phase}</td>
                        <td style={{ ...S.td, color: "#ccc" }}>{row.when}</td>
                        <td style={{ ...S.td, fontFamily: "monospace", fontWeight: "600", color: row.accent }}>{row.cost}</td>
                        <td style={{ ...S.td, color: "#aaa", fontSize: "12px" }}>{row.items}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default MyTrackerScreen;